import classNames from 'classnames';
import * as React from 'react';

import { ClassSelector } from 'bones/components/molecules/QueryBuilder/ClassSelector';
import { DatatypeValue } from 'bones/components/molecules/QueryBuilder/DatatypeValue';
import {
  useGetClassValueItems,
  useGetComparisonPropertyItems,
  useGetComparisonSubjectItems,
  useGetPropertyItemsForClass,
  useValidClassSubjectItems,
} from 'bones/components/molecules/QueryBuilder/hooks/selectorItems';
import { useQueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { InstanceValue } from 'bones/components/molecules/QueryBuilder/InstanceValue';
import { OperatorSelector } from 'bones/components/molecules/QueryBuilder/OperatorSelector';
import { PropertySelector } from 'bones/components/molecules/QueryBuilder/PropertySelector';
import { RDF_TYPE_IRI } from 'bones/constants/iris';
import {
  DatatypeOperatorType,
  DatatypeOperatorTypeValue,
  Direction,
  OperatorType,
} from 'bones/utils/queryBuilder/constants';
import { PropertySubSelectItem } from 'bones/utils/queryBuilder/helpers/select';
import { Variable } from 'bones/utils/queryBuilder/types';

export enum FilterConnectorType {
  LEFT_ARROW = 'left-arrow',
  RIGHT_ARROW = 'right-arrow',
  TAIL = 'tail',
  BLANK = 'blank',
}

export interface FilterConnectorProps {
  type: FilterConnectorType;
  invalid?: boolean;
}
const FilterConnector: React.VFC<FilterConnectorProps> = ({
  type,
  invalid,
}) => {
  const { testIds } = useQueryBuilderSchemaContext();
  return (
    <div
      className={classNames(
        'sd-query-builder-filter-connector',
        `sd-query-builder-filter-connector--${type}`,
        {
          'sd-query-builder-filter-connector--invalid': invalid,
        }
      )}
      data-testid={testIds.filterConnector}
    />
  );
};

export interface FilterRowProps {
  subExpressionIdx: number;
  expressionIdx: number;
  filterIdx: number;
  onSelectComparisonSubject: (item: Variable) => void;
  onSelectComparisonProperty: (item: PropertySubSelectItem) => void;
  onSelectSubject: (item: Variable) => void;
  onSelectProperty: (item: PropertySubSelectItem) => void;
  onSelectOperator: (
    item: DatatypeOperatorTypeValue,
    comparison: boolean
  ) => void;
  onSetValue: (value: string | Variable) => void;
}

export const FilterRow: React.VFC<FilterRowProps> = ({
  subExpressionIdx,
  expressionIdx,
  filterIdx,
  onSelectComparisonSubject,
  onSelectComparisonProperty,
  onSelectSubject,
  onSelectProperty,
  onSelectOperator,
  onSetValue,
}) => {
  const { testIds } = useQueryBuilderSchemaContext();
  const { topExpression } = useQueryBuilderContext();
  const subExpression = topExpression.expressions[subExpressionIdx];
  const expression = subExpression.expressions[expressionIdx];
  const filter = expression.filters[filterIdx];

  const subjectItems = useValidClassSubjectItems({
    subExpressionIdx,
    expressionIdx,
    filterIdx,
  });
  const getPropertyItems = useGetPropertyItemsForClass();
  const propertyItems = getPropertyItems(filter.subject?.iri || '', {
    subExpressionIdx,
    expressionIdx,
    filterIdx,
  });
  const getComparisonSubjectItems = useGetComparisonSubjectItems();
  const comparisonSubjectItems = getComparisonSubjectItems(filter.operator, {
    subExpressionIdx,
    expressionIdx,
    filterIdx,
  });
  const getComparisonPropertyItems = useGetComparisonPropertyItems();
  const comparisonPropertyItems = getComparisonPropertyItems(
    filter.comparisonSubject?.iri || '',
    filter.operator
  );
  const getValueItems = useGetClassValueItems();
  const valueItems = getValueItems(filter.operator, {
    subExpressionIdx,
    expressionIdx,
    filterIdx,
  });

  return (
    <div
      className="sd-query-builder-filter"
      data-testid={testIds.filterContainer}
    >
      <ClassSelector
        invalid={filter.isInvalid.subject}
        items={subjectItems}
        onSelect={({ iri, value }) => {
          onSelectSubject({ iri, id: value });
        }}
        selected={filter.subject}
        testId={testIds.domainClassSelector}
        tooltip={filter.tooltip.subject}
      />
      {filter.subject ? (
        <>
          {filter.operator?.operatorType === OperatorType.INSTANCE ||
          filter.operator?.iri === RDF_TYPE_IRI ? (
            <FilterConnector type={FilterConnectorType.BLANK} />
          ) : (
            <FilterConnector
              invalid={filter.isInvalid.subject}
              type={
                filter.operator &&
                filter.operator.operatorType === OperatorType.OBJECT &&
                filter.operator.direction === Direction.INBOUND
                  ? FilterConnectorType.LEFT_ARROW
                  : FilterConnectorType.TAIL
              }
            />
          )}
          <PropertySelector
            invalid={filter.isInvalid.property}
            items={propertyItems}
            onSelect={onSelectProperty}
            operator={filter.operator}
            subject={filter.subject}
            testId={testIds.propertySelector}
            tooltip={filter.tooltip.property}
          />
        </>
      ) : null}
      {filter.subject &&
      filter.operator?.operatorType === OperatorType.OBJECT ? (
        <>
          {filter.operator.iri === RDF_TYPE_IRI ? (
            <FilterConnector type={FilterConnectorType.BLANK} />
          ) : (
            <FilterConnector
              invalid={filter.isInvalid.value}
              type={
                filter.operator.direction === Direction.INBOUND
                  ? FilterConnectorType.TAIL
                  : FilterConnectorType.RIGHT_ARROW
              }
            />
          )}
          <ClassSelector
            invalid={filter.isInvalid.value}
            items={valueItems}
            onSelect={({ iri, value, text, isNewlyCreated }) => {
              onSetValue({
                iri,
                id: value,
                newLabel: isNewlyCreated ? text : undefined,
                isNewlyCreated,
              });
            }}
            operator={filter.operator}
            selected={filter.value as Variable | null}
            testId={testIds.rangeClassSelector}
            tooltip={filter.tooltip.value}
            useVariableLabel={filter.operator.iri !== RDF_TYPE_IRI}
          />
        </>
      ) : null}
      {filter.subject &&
      filter.operator?.operatorType === OperatorType.INSTANCE ? (
        <>
          <FilterConnector type={FilterConnectorType.BLANK} />
          <InstanceValue
            invalid={filter.isInvalid.value}
            onSetValue={onSetValue}
            subject={filter.subject.iri}
            tooltip={filter.tooltip.value}
            value={filter.value as string | null}
          />
        </>
      ) : null}
      {filter.subject &&
      filter.operator?.operatorType === OperatorType.DATATYPE ? (
        <OperatorSelector
          invalid={filter.isInvalid.operator}
          onSelect={onSelectOperator}
          operatorComparison={filter.operator.comparison}
          operatorType={filter.operator.type}
          range={filter.operator.range}
        />
      ) : null}
      {filter.subject && filter.operator?.comparison && filter.operator.type ? (
        <ClassSelector
          invalid={filter.isInvalid.comparisonSubject}
          items={comparisonSubjectItems}
          onSelect={({ iri, value }) => {
            onSelectComparisonSubject({ iri, id: value });
          }}
          selected={filter.comparisonSubject || null}
          testId={testIds.comparisonClassSelector}
          tooltip={filter.tooltip.comparisonSubject}
        />
      ) : null}
      {filter.subject &&
      filter.operator?.comparison &&
      filter.operator.type &&
      filter.comparisonSubject ? (
        <>
          <FilterConnector
            invalid={filter.isInvalid.comparisonSubject}
            type={FilterConnectorType.TAIL}
          />
          <PropertySelector
            invalid={filter.isInvalid.comparisonProperty}
            items={comparisonPropertyItems}
            onSelect={onSelectComparisonProperty}
            operator={filter.comparisonOperator || null}
            subject={filter.comparisonSubject}
            testId={testIds.comparisonPropertySelector}
            tooltip={filter.tooltip.comparisonProperty}
          />
        </>
      ) : null}
      {filter.subject &&
      !filter.operator?.comparison &&
      filter.operator?.operatorType === OperatorType.DATATYPE &&
      filter.operator.type &&
      filter.operator.type !== DatatypeOperatorType.EXISTS &&
      filter.operator.type !== DatatypeOperatorType.DOES_NOT_EXIST ? (
        <DatatypeValue
          invalid={filter.isInvalid.value}
          onSetValue={onSetValue}
          operatorType={filter.operator.type}
          predicate={filter.operator.iri}
          range={filter.operator.range}
          subject={filter.subject.iri}
          value={filter.value as string | null}
        />
      ) : null}
    </div>
  );
};
